// src/services/pmsSyncService.js
// PMS (Practice Management System) sync service
// Supports: CSV Import, Open Dental API, and bot-based scrapers (WaveOrtho, Eaglesoft, Dentrix Ascend, Carestream)

import { supabase } from '../lib/supabaseClient';
import * as awsApi from './awsApiService';

const useAWS = (process.env.REACT_APP_USE_AWS || '').trim() === 'true';

/**
 * PMS System Types
 */
export const PMS_SYSTEMS = {
  OPEN_DENTAL:    'open_dental',
  WAVEORTHO:      'waveortho',
  EAGLESOFT:      'eaglesoft',
  DENTRIX_ASCEND: 'dentrix-ascend',
  CARESTREAM:     'carestream',
  CSV_IMPORT:     'csv_import',
};

export const BOT_PMS_SYSTEMS = ['waveortho', 'eaglesoft', 'dentrix-ascend', 'carestream'];

/**
 * Get PMS configuration for a practice
 */
export const getPMSConfig = async (practiceId) => {
  try {
    if (useAWS) {
      // AWS mode: use API to get practice
      const result = await awsApi.getPractice();
      if (result.success && result.data) {
        return {
          system: result.data.pms_system || PMS_SYSTEMS.CSV_IMPORT,
          config: result.data.pms_config || {},
          isConfigured: !!result.data.pms_system
        };
      }
      return {
        system: PMS_SYSTEMS.CSV_IMPORT,
        config: {},
        isConfigured: false
      };
    }

    // Supabase mode
    const { data, error } = await supabase
      .from('practices')
      .select('pms_system, pms_config')
      .eq('id', practiceId)
      .single();

    if (error) throw error;

    return {
      system: data?.pms_system || PMS_SYSTEMS.CSV_IMPORT,
      config: data?.pms_config || {},
      isConfigured: !!data?.pms_system
    };
  } catch (error) {
    console.error('Error fetching PMS config:', error);
    return {
      system: PMS_SYSTEMS.CSV_IMPORT,
      config: {},
      isConfigured: false
    };
  }
};

/**
 * Save PMS configuration for a practice
 */
export const savePMSConfig = async (practiceId, pmsSystem, config) => {
  try {
    if (useAWS) {
      return await awsApi.saveOpenDentalConfig(pmsSystem, config);
    }

    const { error } = await supabase
      .from('practices')
      .update({
        pms_system: pmsSystem,
        pms_config: config,
        updated_at: new Date().toISOString()
      })
      .eq('id', practiceId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Test PMS connection
 */
export const testPMSConnection = async (pmsSystem, config) => {
  if (pmsSystem === PMS_SYSTEMS.OPEN_DENTAL) {
    if (useAWS) {
      return await awsApi.testOpenDentalConnection(config.apiUrl, config.apiKey);
    }
    return await testOpenDentalConnectionDirect(config);
  }

  return {
    success: false,
    message: 'Please use CSV import for your PMS system'
  };
};

/**
 * Test Open Dental API connection (direct, non-AWS fallback)
 */
const testOpenDentalConnectionDirect = async (config) => {
  const { apiUrl, apiKey } = config;
  if (!apiUrl || !apiKey) {
    return { success: false, message: 'Missing API URL or API Key.' };
  }
  try { new URL(apiUrl); } catch {
    return { success: false, message: 'Invalid API URL format. Example: https://your-server.com:port' };
  }
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    const res = await fetch(`${apiUrl}/api/patients?limit=1`, {
      headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (res.ok) return { success: true, message: 'Connection successful!' };
    if (res.status === 401) return { success: false, message: 'Authentication failed. Check your API key.' };
    return { success: false, message: `Connection failed: HTTP ${res.status}` };
  } catch (error) {
    return { success: false, message: error.name === 'AbortError' ? 'Connection timed out.' : `Connection error: ${error.message}` };
  }
};

/**
 * Sync patients from PMS to database
 */
export const syncPatientsFromPMS = async (practiceId) => {
  const pmsConfig = await getPMSConfig(practiceId);
  if (pmsConfig.system === PMS_SYSTEMS.CSV_IMPORT || !pmsConfig.isConfigured) {
    return { success: false, message: 'Please use the CSV upload feature to import patients.' };
  }
  if (pmsConfig.system === PMS_SYSTEMS.OPEN_DENTAL) {
    if (useAWS) {
      const result = await awsApi.syncOpenDentalData('patients');
      return {
        success: result.success,
        message: result.error || null,
        results: result.results?.patients
          ? { successful: result.results.patients.inserted + result.results.patients.updated, failed: result.results.patients.failed }
          : null
      };
    }
  }
  return { success: false, message: 'Unknown PMS system configured' };
};

/**
 * Sync claims from PMS to database
 */
export const syncClaimsFromPMS = async (practiceId) => {
  const pmsConfig = await getPMSConfig(practiceId);
  if (pmsConfig.system === PMS_SYSTEMS.CSV_IMPORT || !pmsConfig.isConfigured) {
    return { success: false, message: 'Please use the CSV upload feature to import claims.' };
  }
  if (pmsConfig.system === PMS_SYSTEMS.OPEN_DENTAL) {
    if (useAWS) {
      const result = await awsApi.syncOpenDentalData('claims');
      return {
        success: result.success,
        message: result.error || null,
        results: result.results?.claims
          ? { successful: result.results.claims.inserted + result.results.claims.updated, failed: result.results.claims.failed }
          : null
      };
    }
  }
  return { success: false, message: 'Unknown PMS system configured' };
};

/**
 * Get last sync time for a practice
 */
export const getLastSyncTime = async (practiceId) => {
  try {
    if (useAWS) {
      // AWS mode: use API to get practice
      const result = await awsApi.getPractice();
      if (result.success && result.data) {
        return result.data.last_pms_sync || null;
      }
      return null;
    }

    // Supabase mode
    const { data, error } = await supabase
      .from('practices')
      .select('last_pms_sync')
      .eq('id', practiceId)
      .single();

    if (error) throw error;

    return data?.last_pms_sync || null;
  } catch (error) {
    console.error('Error fetching last sync time:', error);
    return null;
  }
};

/**
 * Update last sync time
 */
export const updateLastSyncTime = async (practiceId) => {
  try {
    if (useAWS) {
      // Lambda updates last_pms_sync automatically after sync — no-op here
      return { success: true };
    }

    const { error } = await supabase
      .from('practices')
      .update({ last_pms_sync: new Date().toISOString() })
      .eq('id', practiceId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

/**
 * Get available PMS systems
 */
export const getAvailablePMSSystems = () => {
  return [
    {
      id: PMS_SYSTEMS.WAVEORTHO,
      name: 'WaveOrtho',
      description: 'Cloud-based orthodontic practice management system',
      status: 'available',
      requiresApiKey: false,
      compatibility: 'WaveOrtho practices',
      setupTime: '2 minutes',
      icon: '🦷',
      note: 'Enter your WaveOrtho login — FlowRCM syncs automatically every night'
    },
    {
      id: PMS_SYSTEMS.EAGLESOFT,
      name: 'Eaglesoft',
      description: 'Patterson Dental Eaglesoft web portal sync',
      status: 'available',
      requiresApiKey: false,
      compatibility: 'Eaglesoft practices',
      setupTime: '2 minutes',
      icon: '🦅',
      note: 'Enter your Eaglesoft portal login — FlowRCM syncs automatically every night'
    },
    {
      id: PMS_SYSTEMS.DENTRIX_ASCEND,
      name: 'Dentrix Ascend',
      description: 'Henry Schein Dentrix Ascend cloud sync',
      status: 'available',
      requiresApiKey: false,
      compatibility: 'Dentrix Ascend practices',
      setupTime: '2 minutes',
      icon: '🏥',
      note: 'Enter your Dentrix Ascend login — FlowRCM syncs automatically every night'
    },
    {
      id: PMS_SYSTEMS.CARESTREAM,
      name: 'Carestream Dental',
      description: 'Carestream Dental web portal sync',
      status: 'available',
      requiresApiKey: false,
      compatibility: 'Carestream practices',
      setupTime: '2 minutes',
      icon: '🏥',
      note: 'Enter your Carestream login — FlowRCM syncs automatically every night'
    },
    {
      id: PMS_SYSTEMS.CSV_IMPORT,
      name: 'CSV Import',
      description: 'Works with all PMS systems - export CSV from your PMS and upload here',
      status: 'available',
      requiresApiKey: false,
      compatibility: 'Universal — any PMS with CSV export',
      setupTime: 'Immediate',
      icon: '📄'
    },
  ];
};

/**
 * Check if PMS system supports automatic sync
 */
export const supportsAutomaticSync = (pmsSystem) => {
  return pmsSystem === PMS_SYSTEMS.OPEN_DENTAL;
};